/* ==========================================================
   ESSENCE STRICT SEARCH V3 — PRECISION + RECALL LAYER
   Chez Mishel / Shopify Prestige

   Purpose:
   - Preserve the buyer-facing query and URL exactly as typed.
   - Keep one-word text searches broad across title + tag.
   - Multi-word text searches:
       * text-only: exact title phrase OR all title terms by prefix.
       * text + numeric attribute: exact phrase across title/tag,
         including common apostrophe variants, to keep product
         type and numeric attribute contextually linked.
   - Preserve native numeric / SKU-like search behavior.
   - Keep Strict Search V1 render, sorting and infinite-scroll
     architecture intact.
========================================================== */

(() => {
  'use strict';

  if (window.__essenceStrictSearchV3Loaded) {
    return;
  }

  window.__essenceStrictSearchV3Loaded = true;

  const SEARCH_PATH_RE = /\/search\/?$/;
  const INTERNAL_SECTION_PARAM = 'section_id';
  const MAX_QUERY_LENGTH = 180;
  const MAX_TERMS = 12;
  const MAX_PHRASE_VARIANTS = 10;
  const APOSTROPHE_VARIANTS = ["'", '’', '׳'];

  const normalizeWhitespace = (value) =>
    String(value || '')
      .replace(/\s+/gu, ' ')
      .trim();

  const isSkuLikeQuery = (query) => {
    const normalized = normalizeWhitespace(query);

    return (
      normalized.length > 0 &&
      /\d/u.test(normalized) &&
      /^[\d\s._\-/]+$/u.test(normalized)
    );
  };

  const escapeSearchPhrase = (value) =>
    value
      .replace(/\\/gu, '\\\\')
      .replace(/"/gu, '\\"');

  const escapePrefixTerm = (value) =>
    value.replace(/([:\\()])/gu, '\\$1');

  const stripTrailingApostrophe = (value) =>
    value.replace(/['’׳]+$/gu, '');

  const tokenizeTextQuery = (query) => {
    const normalized = normalizeWhitespace(query).slice(0, MAX_QUERY_LENGTH);

    if (!normalized) {
      return [];
    }

    return normalized
      .split(' ')
      .map((term) =>
        term
          .replace(/^[,.;!?()[\]{}<>]+/gu, '')
          .replace(/[,.;!?()[\]{}<>]+$/gu, '')
          .trim()
      )
      .filter(Boolean)
      .slice(0, MAX_TERMS);
  };

  const hasNumericTerm = (terms) => terms.some((term) => /\d/u.test(term));

  const buildPhraseVariants = (terms) => {
    const baseTerms = terms.map(stripTrailingApostrophe);
    const variants = new Set([
      normalizeWhitespace(terms.join(' ')),
      normalizeWhitespace(baseTerms.join(' '))
    ]);

    for (let index = 0; index < baseTerms.length; index += 1) {
      const term = baseTerms[index];

      if (!term || /\d/u.test(term) || !/\p{L}/u.test(term)) {
        continue;
      }

      for (const apostrophe of APOSTROPHE_VARIANTS) {
        const candidate = [...baseTerms];
        candidate[index] = `${term}${apostrophe}`;
        variants.add(normalizeWhitespace(candidate.join(' ')));

        if (variants.size >= MAX_PHRASE_VARIANTS) {
          return Array.from(variants);
        }
      }
    }

    return Array.from(variants);
  };

  const buildNumericMultiwordQuery = (terms) => {
    const variants = buildPhraseVariants(terms);
    const clauses = [];

    variants.forEach((phrase) => {
      const escaped = escapeSearchPhrase(phrase);
      clauses.push(`title:"${escaped}"`);
      clauses.push(`tag:"${escaped}"`);
    });

    return `(${clauses.join(' OR ')})`;
  };

  const buildTextMultiwordQuery = (terms) => {
    const exactPhrase = escapeSearchPhrase(terms.join(' '));

    const allTitlePrefixes = terms
      .map((term) => stripTrailingApostrophe(term))
      .filter(Boolean)
      .map((term) => `title:${escapePrefixTerm(term)}*`)
      .join(' AND ');

    return `(title:"${exactPhrase}" OR (${allTitlePrefixes}))`;
  };

  const buildStrictQuery = (query) => {
    const terms = tokenizeTextQuery(query);

    if (!terms.length) {
      return '';
    }

    if (terms.length === 1) {
      const phrase = escapeSearchPhrase(terms[0]);
      return `(title:"${phrase}" OR tag:"${phrase}")`;
    }

    if (hasNumericTerm(terms)) {
      return buildNumericMultiwordQuery(terms);
    }

    return buildTextMultiwordQuery(terms);
  };

  const shouldUseStrictSearch = (query) =>
    Boolean(query) && !isSkuLikeQuery(query) && Boolean(buildStrictQuery(query));

  const getBuyerQuery = () => {
    try {
      return normalizeWhitespace(
        new URL(window.location.href).searchParams.get('q')
      );
    } catch (error) {
      return '';
    }
  };

  const cleanVisibleQueryArtifacts = () => {
    const buyerQuery = getBuyerQuery();

    if (!buyerQuery || !shouldUseStrictSearch(buyerQuery)) {
      return;
    }

    const technicalQuery = buildStrictQuery(buyerQuery);

    if (!technicalQuery) {
      return;
    }

    const replaceTechnicalText = (element) => {
      if (!(element instanceof Element)) {
        return;
      }

      const currentText = element.textContent || '';

      if (currentText.includes(technicalQuery)) {
        element.textContent = currentText.split(technicalQuery).join(buyerQuery);
      }
    };

    document
      .querySelectorAll(
        '.shopify-section--main-search .section-header .v-stack > p, ' +
        '.shopify-section--main-search .empty-state .prose p'
      )
      .forEach(replaceTechnicalText);
  };

  const scheduleVisibleQueryCleanup = () => {
    window.requestAnimationFrame(cleanVisibleQueryArtifacts);
  };

  const replacePublicApi = () => {
    const currentApi = window.EssenceStrictSearch;

    if (!currentApi) {
      return;
    }

    window.EssenceStrictSearch = Object.freeze({
      ...currentApi,
      version: '3.0.1',
      normalizeWhitespace,
      isSkuLikeQuery,
      tokenizeTextQuery,
      buildStrictQuery,
      shouldUseStrictSearch
    });
  };

  const nativeFetch = window.fetch.bind(window);

  window.fetch = (input, init) => {
    try {
      const inputUrl =
        input instanceof Request
          ? input.url
          : input instanceof URL
            ? input.toString()
            : String(input);

      const url = new URL(inputUrl, window.location.href);

      if (
        url.origin === window.location.origin &&
        SEARCH_PATH_RE.test(url.pathname) &&
        url.searchParams.has(INTERNAL_SECTION_PARAM)
      ) {
        const buyerQuery = getBuyerQuery();

        if (shouldUseStrictSearch(buyerQuery)) {
          url.searchParams.set('q', buildStrictQuery(buyerQuery));

          if (input instanceof Request) {
            input = new Request(url.toString(), input);
          } else {
            input = url.toString();
          }
        }
      }
    } catch (error) {
      // Fail open: keep the request generated by Strict Search V1.
    }

    return nativeFetch(input, init);
  };

  replacePublicApi();

  document.documentElement.addEventListener(
    'theme:loading:end',
    scheduleVisibleQueryCleanup
  );

  scheduleVisibleQueryCleanup();

  // Strict Search V1 can start its first render before this deferred file
  // executes. Re-render once through the V3 request guard so the preview
  // always settles on the V3 query semantics.
  if (
    SEARCH_PATH_RE.test(window.location.pathname) &&
    window.EssenceStrictSearch &&
    typeof window.EssenceStrictSearch.render === 'function'
  ) {
    void window.EssenceStrictSearch.render().finally(scheduleVisibleQueryCleanup);
  }
})();
